// Sprint runner-ssot-unification PR-1 — authoritative usage cache store.
//
// INV-X (docs/architecture.md): every UI surface that renders runner
// capacity reads from this store, which polls `GET /api/runners/usage`
// (the S20.8 real-Anthropic-data cache). The sidebar pill, the modal,
// the RunnerChip tooltip — all read from the same Zustand slice.
//
// Lifecycle: `startRunnersUsagePolling()` is called once at the page
// mount (HomePage, next to `startRunnersPolling()`). Same
// visibility-aware pattern: hidden tabs skip the fetch.

import { create } from 'zustand';
import {
  fetchRunnersUsage,
  type RunnerUsageEntry,
} from '../lib/api';

const POLL_INTERVAL_MS = 60_000;

export interface RunnersUsageState {
  entriesByRunnerId: Record<string, RunnerUsageEntry>;
  loaded: boolean;
  error: string | null;

  hydrate: (opts?: { fresh?: boolean }) => Promise<RunnerUsageEntry[]>;
  _setEntries: (entries: RunnerUsageEntry[]) => void;
  resetForTests: () => void;
}

export const useRunnersUsageStore = create<RunnersUsageState>((set) => ({
  entriesByRunnerId: {},
  loaded: false,
  error: null,

  hydrate: async (opts) => {
    try {
      const res = await fetchRunnersUsage({ fresh: opts?.fresh });
      const map: Record<string, RunnerUsageEntry> = {};
      for (const r of res.runners) map[r.runnerId] = r;
      set({ entriesByRunnerId: map, loaded: true, error: null });
      return res.runners;
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Could not load usage' });
      return [];
    }
  },

  _setEntries: (entries) =>
    set(() => {
      const map: Record<string, RunnerUsageEntry> = {};
      for (const r of entries) map[r.runnerId] = r;
      return { entriesByRunnerId: map, loaded: true, error: null };
    }),

  resetForTests: () => set({ entriesByRunnerId: {}, loaded: false, error: null }),
}));

export function startRunnersUsagePolling(intervalMs: number = POLL_INTERVAL_MS): () => void {
  void useRunnersUsageStore.getState().hydrate();
  const id = window.setInterval(() => {
    if (document.visibilityState === 'visible') {
      void useRunnersUsageStore.getState().hydrate();
    }
  }, intervalMs);
  return () => window.clearInterval(id);
}
